import React, { useState, useRef, useEffect } from 'react';
import FeatureGate from './FeatureGate';
import { useCredits } from '../../../hooks/useCredits';

/**
 * A wrapper component that gates any action behind a credit check
 * Shows upgrade modal if insufficient credits, otherwise executes the action
 */
const CreditGatedAction = ({ operation, creditsRequired = 1, onAction, children, referenceId = null }) => {
  const { consumeCredits } = useCredits();
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleAction = async e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isProcessing) return; // Prevent double-clicks

    try {
      setIsProcessing(true);

      // Consume credits first
      await consumeCredits(operation, creditsRequired, referenceId);

      // If successful and component is still mounted, execute the actual action
      if (isMountedRef.current && onAction) {
        await onAction();
      }
    } catch (error) {
      console.error('Credit gated action error:', error);
      // FeatureGate will handle showing upgrade modal for insufficient credits
      if (!error.message.includes('Insufficient credits')) {
        throw error; // Re-throw non-credit errors
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
      }
    }
  };

  return (
    <FeatureGate
      operation={operation}
      creditsRequired={creditsRequired}
      showCreditCost={false} // Don't show cost in action buttons
    >
      <div
        onClick={handleAction}
        style={{ cursor: isProcessing ? 'wait' : 'pointer' }}
        role='button'
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleAction(e);
          }
        }}
      >
        {children}
      </div>
    </FeatureGate>
  );
};

export default CreditGatedAction;
